import type { ImpactSeverity } from './impact.js';

/**
 * Análise estática do SQL da biblioteca (spec/03-OPERATIONS-CATALOG.md §2.13).
 * Não é um parser completo de Postgres — é um guarda conservador: na dúvida, trata como escrita.
 */

const WRITE_KEYWORDS = [
  'insert',
  'update',
  'delete',
  'truncate',
  'drop',
  'alter',
  'create',
  'grant',
  'revoke',
  'copy',
  'vacuum',
  'reindex',
  'refresh',
  'call',
  'do',
];

export interface SqlAnalysis {
  /** SQL sem comentários e sem `;` final, usado para as checagens. */
  normalized: string;
  statementCount: number;
  isMultiStatement: boolean;
  writeKeywords: string[];
  isReadOnly: boolean;
  /** Postgres aceita EXPLAIN neste statement (SELECT/INSERT/UPDATE/DELETE/WITH/…). */
  isExplainable: boolean;
  severity: ImpactSeverity;
}

/** Prefixo aceito por `EXPLAIN` no Postgres (não inclui DO/CREATE/CALL/…). */
const EXPLAINABLE_PREFIX =
  /^\s*(with|select|insert|update|delete|merge|values|table)\b/i;

export function stripSqlComments(sql: string): string {
  return sql
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\n]*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Divide em statements ignorando `;` dentro de literais e de dollar-quoting. */
export function splitStatements(sql: string): string[] {
  const statements: string[] = [];
  let current = '';
  let inSingle = false;
  let inDouble = false;
  let dollarTag: string | null = null;

  for (let i = 0; i < sql.length; i += 1) {
    const char = sql[i];

    if (dollarTag) {
      current += char;
      if (sql.startsWith(dollarTag, i)) {
        current += sql.slice(i + 1, i + dollarTag.length);
        i += dollarTag.length - 1;
        dollarTag = null;
      }
      continue;
    }

    if (!inSingle && !inDouble && char === '$') {
      const match = /^\$[A-Za-z_]*\$/.exec(sql.slice(i));
      if (match) {
        dollarTag = match[0];
        current += dollarTag;
        i += dollarTag.length - 1;
        continue;
      }
    }

    if (char === "'" && !inDouble) inSingle = !inSingle;
    else if (char === '"' && !inSingle) inDouble = !inDouble;

    if (char === ';' && !inSingle && !inDouble) {
      if (current.trim()) statements.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) statements.push(current.trim());
  return statements;
}

export function analyzeSql(sql: string): SqlAnalysis {
  const normalized = stripSqlComments(sql);
  const statements = splitStatements(normalized);
  const lower = normalized.toLowerCase();

  const writeKeywords = WRITE_KEYWORDS.filter((keyword) =>
    new RegExp(`(^|[^a-z_])${keyword}\\s`, 'i').test(lower),
  );

  // `WITH ... INSERT` e afins já são pegos acima; `SELECT ... FOR UPDATE` também escreve lock.
  if (/\sfor\s+update\b/.test(lower) && !writeKeywords.includes('update')) {
    writeKeywords.push('update');
  }

  const isReadOnly = writeKeywords.length === 0 && /^\s*(select|with|explain|show|table)\b/i.test(normalized);
  const looksTruncated = /\.\.\.\s*$/.test(normalized);
  const isExplainable =
    statements.length === 1 && !looksTruncated && EXPLAINABLE_PREFIX.test(normalized);

  return {
    normalized,
    statementCount: statements.length,
    isMultiStatement: statements.length > 1,
    writeKeywords,
    isReadOnly,
    isExplainable,
    severity: isReadOnly ? 'read-only' : 'destructive',
  };
}

export function isSelectOnly(sql: string): boolean {
  return analyzeSql(sql).isReadOnly;
}

/** Motivo pt-BR quando EXPLAIN não se aplica (UI / API). */
export function explainUnsupportedReason(sql: string): string | null {
  const analysis = analyzeSql(sql);
  if (!analysis.normalized.trim()) return 'Informe um statement SQL.';
  if (analysis.isMultiStatement || analysis.statementCount !== 1) {
    return 'Informe um único statement SQL (sem multi-statement).';
  }
  if (/\.\.\.\s*$/.test(analysis.normalized)) {
    return 'O SQL parece truncado (termina em “…”). Cole o statement completo para rodar EXPLAIN.';
  }
  if (analysis.isExplainable) return null;
  if (/^\s*do\b/i.test(analysis.normalized)) {
    return 'Postgres não aceita EXPLAIN em blocos DO $$ … $$. Use SELECT/INSERT/UPDATE/DELETE (ou WITH). Para DDL, inspecione índices abaixo.';
  }
  return 'EXPLAIN só funciona com SELECT, INSERT, UPDATE, DELETE, MERGE, VALUES, TABLE ou WITH. DO/CREATE/CALL e afins não são suportados.';
}

/** Heurística leve: primeira tabela após FROM/JOIN (sem schema). Fallback = null. */
export function guessTableFromSql(sql: string): string | null {
  const match =
    /\b(?:from|join)\s+(?:["']?([a-zA-Z_][a-zA-Z0-9_]*)["']?\.)?["']?([a-zA-Z_][a-zA-Z0-9_]*)["']?/i.exec(
      sql,
    );
  return match?.[2] ?? null;
}

export function parseExplainPlanFlags(planText: string): {
  hasSeqScan: boolean;
  hasIndexScan: boolean;
  hasBitmapIndexScan: boolean;
} {
  const lower = planText.toLowerCase();
  return {
    hasSeqScan: lower.includes('seq scan'),
    hasIndexScan: lower.includes('index scan'),
    hasBitmapIndexScan: lower.includes('bitmap index scan'),
  };
}
